/**
 * SiteFooter — slim, link-only footer.
 *
 * Used at the bottom of long-form pages (currently /blog) where we
 * want a fast way to reach Terms, Privacy, Contact, and the social
 * accounts without forcing the user to scroll back up to the header.
 *
 * Kept intentionally minimal — no newsletter form here, no big
 * branding block. The landing-page LinktreeHub already serves as
 * the "front door" with the bigger CTAs.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SocialLinks from './SocialLinks';

export const SiteFooter = () => {
  const { isSpanish } = useLanguage();

  // Per user rule (May 2026): footer links MUST be alphabetical L→R
  // (About → Contact → FAQs → Privacy → Safety → Terms). Add new
  // links in their alphabetic slot — do NOT just append.
  const legalLinks = [
    { to: '/about',    en: 'About',           es: 'Acerca de' },
    { to: '/feedback', en: 'Contact',         es: 'Contacto' },
    { to: '/faqs',     en: 'FAQs',            es: 'Preguntas' },
    { to: '/press',    en: 'Press',           es: 'Prensa' },
    { to: '/privacy',  en: 'Privacy',         es: 'Privacidad' },
    { to: '/safety',   en: 'Safety',          es: 'Seguridad' },
    { to: '/terms',    en: 'Terms',           es: 'Términos' },
  ];

  return (
    <footer
      className="border-t border-border mt-16 bg-muted/30 pb-20 md:pb-0"
      data-testid="site-footer"
    >
      <div className="container max-w-5xl mx-auto px-4 py-10">
        <div className="flex flex-col items-center gap-6 text-center">
          {/* Brand line */}
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">ClearCommunicationApp</span>
            {' · '}
            {isSpanish
              ? 'Una herramienta de apoyo'
              : 'A supportive tool'}
          </p>

          {/* Social tags row — reuses the shared SocialLinks pill set
              (Facebook, Instagram, X, LinkedIn, TikTok, YouTube) */}
          <SocialLinks />

          {/* "As Featured On" — third-party press / directory listings
              that reviewed or listed the app. Keeps the footer trustworthy
              without overcrowding. New listings are appended in
              chronological order so the freshest mention is right-most. */}
          <div className="flex flex-col items-center gap-1.5" data-testid="footer-as-featured-on">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium">
              {isSpanish ? 'Destacado en' : 'As Featured On'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs">
              <a
                href="https://neurodivergenttech.org/tools/clearcommunicationapp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-[#00A896] underline-offset-2 hover:underline transition-colors"
                data-testid="featured-on-neurodivergent-tech"
              >
                Neurodivergent Tech
              </a>
              <span className="text-muted-foreground/30" aria-hidden="true">·</span>
              <a
                href="https://chronicallycozylife.com/listing/clearcommunicationapp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-[#00A896] underline-offset-2 hover:underline transition-colors"
                data-testid="featured-on-chronically-cozy-life"
              >
                Chronically Cozy Life
              </a>
            </div>
          </div>

          {/* Small Google-branded review CTA — matches the badge style
              used on the Linktree (white square + multi-coloured G on
              left, Google blue panel with text on right). Compact pill
              shape so it doesn't dominate the footer. */}
          <a
            href="https://g.page/r/CZURemrhanjoEAE/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-google-review"
            className="inline-flex rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-[#4285F4]"
          >
            <span className="shrink-0 w-8 h-8 bg-white flex items-center justify-center" aria-hidden="true">
              <svg viewBox="0 0 48 48" className="h-4 w-4">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            </span>
            <span className="bg-[#4285F4] hover:bg-[#3367D6] text-white text-xs font-semibold px-3 h-8 flex items-center">
              {isSpanish ? 'Reseña en Google' : 'Review us on Google'}
            </span>
          </a>

          {/* Legal + navigation links */}
          <nav
            aria-label={isSpanish ? 'Pie de página' : 'Footer navigation'}
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs"
          >
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`footer-link-${link.to.replace('/', '')}`}
              >
                {isSpanish ? link.es : link.en}
              </Link>
            ))}
          </nav>

          {/* Compliance line */}
          <p className="text-[11px] text-muted-foreground leading-relaxed max-w-md">
            {isSpanish ? (
              <>
                Cumple con el{' '}
                <Link to="/terms#eu-geoblocking" className="underline hover:text-foreground">
                  Reglamento (UE) 2018/302
                </Link>{' '}
                · Una herramienta de apoyo. No es una herramienta de diagnóstico y no brindamos consejo médico.
              </>
            ) : (
              <>
                Complies with EU{' '}
                <Link to="/terms#eu-geoblocking" className="underline hover:text-foreground">
                  Geo-blocking Regulation 2018/302
                </Link>{' '}
                · ClearCommunicationApp does not provide diagnostic tools or medical advice.
              </>
            )}
          </p>

          {/* Copyright */}
          <p className="text-[11px] text-muted-foreground">
            © {new Date().getFullYear()} ClearCommunicationApp LLC.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
